import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Grid, Skeleton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { TeamDropdown } from '../../dropdowns/TeamDropdown';
import HighchartsReact from 'highcharts-react-official';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import { IUserModel, IUserAtom } from '../../../../recoil/atoms/user';
import { methodNameEnum } from '../../../../recoil/selectors/makePHPRequest';
import {
  themeModeAtom,
  themeModeEnum,
} from '../../../../recoil/atoms/themeModeAtom';
import { useRecoilValue } from 'recoil';

import { getPercentComparedToAverage } from '../utils/calculations';

export interface AgentBarSeries {
  calls: number;
  connections: number;
  outbound: number;
  inbound: number;
  missedCalls: number;
  sms: number;
  voicemails: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: '24px',
      flexGrow: 1,
    },
    chartContainer: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    callConnections: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      height: '100%',
      padding: '16px',
      minWidth: '175px',
      '& span.MuiSkeleton-root': {
        margin: '10px',
        backgroundColor:
          theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
      },
    },
    statLoader: {
      paddingTop: '50%',
      '& span.MuiSkeleton-root': {
        margin: '10px',
        backgroundColor:
          theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
      },
    },
    chartLoader: {
      '& span.MuiSkeleton-root': {
        margin: '10px',
        backgroundColor:
          theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
      },
    },
  })
);

const StatPanel = ({ type, agentTotal, teamAverage }) => {
  const displayTotal = React.useMemo(() => {
    return `${getPercentComparedToAverage(agentTotal, teamAverage)}%`;
  }, [agentTotal, teamAverage]);
  return (
    <div>
      {type}: {displayTotal}
    </div>
  );
};
function AgentCallMetricsBar(props: {
  agentSeries: AgentBarSeries;
  previousRange: string;
  loading: boolean;
  dateInterval: string;
  userAtomContents: IUserAtom;
  id: number;
  team: { id: number; name: string };
}) {
  const classes = useStyles();
  const {
    agentSeries,
    dateInterval,
    userAtomContents,
    id,
    loading,
    team,
  } = props;
  const [selectedTeamId, setSelectedTeamId] = useState<number>(team?.id ?? 0);
  const [teamName, setTeamName] = useState<string>(team?.name ?? 'All Agents');
  const themeMode = useRecoilValue(themeModeAtom);
  const chartRef = useRef(null);

  const [
    businessUserCurrentCached,
    businessUserCurrentLoadable,
  ] = makeApiRequestCached<IUserModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])?.find(
        (user) => user.userid === +id
      )
  );

  const metricsPayload = useMemo(
    () =>
      selectedTeamId === 0
        ? {
            businessid: userAtomContents?.businessid,
            interval: dateInterval,
            type: 'business',
            id: userAtomContents?.businessid,
            source: 'agent-call-metrics',
          }
        : {
            businessid: userAtomContents?.businessid,
            interval: dateInterval,
            id: selectedTeamId,
            type: 'team',
            source: 'agent-call-metrics',
          },
    [selectedTeamId]
  );

  const [
    teamCallMetricsCached,
    teamCallMetricsLoadable,
    setRefetchToggleForTeamCallMetrics,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_CALL_METRICS,
      payloadJsonString: JSON.stringify(metricsPayload),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.charts ?? cached.current
  );

  useEffect(() => {
    setRefetchToggleForTeamCallMetrics({});
  }, []);

  const roundTwo = (num: number) => Math.round(num * 100) / 100;

  const teamSeries = useMemo<AgentBarSeries>(
    () =>
      teamCallMetricsLoadable.state === 'hasValue'
        ? {
            calls:
              roundTwo(teamCallMetricsCached?.current?.calls?.average) ?? 0,
            connections:
              roundTwo(teamCallMetricsCached?.current?.connections?.average) ??
              0,
            outbound:
              roundTwo(
                teamCallMetricsCached?.current?.calls?.outbound?.averageNumber
                  ?.average
              ) ?? 0,
            inbound:
              roundTwo(
                teamCallMetricsCached?.current?.calls?.inbound?.averageNumber
                  .average
              ) ?? 0,
            missedCalls:
              roundTwo(
                teamCallMetricsCached?.current?.calls?.missed?.average
              ) ?? 0,
            voicemails:
              roundTwo(teamCallMetricsCached?.current?.voicemailsAverage) ?? 0,
            sms: roundTwo(teamCallMetricsCached?.current?.smsAverage) ?? 0,
          }
        : {
            calls: 0,
            connections: 0,
            outbound: 0,
            inbound: 0,
            missedCalls: 0,
            voicemails: 0,
            sms: 0,
          },
    [teamCallMetricsLoadable, teamCallMetricsCached]
  );

  const options = useMemo(
    () => ({
      title: {
        text: '',
      },
      allowChartUpdate: true,
      chart: {
        type: 'column',
        backgroundColor: null,
        '& .highcharts-plot-background': {
          background: '#f7f5f5',
        },
      },
      colors: ['#91D2E8', '#0098C7'],
      credits: {
        enabled: false,
      },
      series: [
        {
          name:
            (businessUserCurrentCached.current?.fname ?? '') +
            ' ' +
            (businessUserCurrentCached.current?.lname ?? ''),
          data: Object.values(agentSeries),
        },
        {
          name: teamName + ' (Average)',
          data: Object.values(teamSeries),
        },
      ],
      xAxis: {
        categories: [
          'Calls',
          'Connections',
          'Outbound Calls',
          'Inbound Calls',
          'Missed Calls',
          'Voicemails left',
          'SMS sent',
        ],
      },
      legend: {
        itemMarginTop: 8,
        itemMarginBottom: 8,
        itemStyle: {
          fontStyle: 'normal',
          color: themeMode === themeModeEnum.DARK_MODE ? '#fff' : '#333333',
        },
        symbolHeight: 16,
        symbolWidth: 16,
      },
    }),
    [agentSeries, teamSeries, businessUserCurrentCached]
  );

  const graphLoader = (
    <Box margin={1} className={classes.chartLoader}>
      <Skeleton variant="rectangular" height={50} />
      <Skeleton variant="rectangular" height={50} />
      <Skeleton variant="rectangular" height={50} />
      <Skeleton variant="rectangular" height={50} />
      <Skeleton variant="rectangular" height={50} />
    </Box>
  );

  const isLoading = useMemo(
    () =>
      loading ||
      teamCallMetricsLoadable?.state === 'loading' ||
      businessUserCurrentLoadable?.state === 'loading',
    [businessUserCurrentLoadable, loading, teamCallMetricsLoadable]
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
          <Box style={{ paddingTop: '16px', width: '100%' }}>
            {isLoading ? (
              <Skeleton
                variant="rectangular"
                height={50}
                style={{ marginTop: '10px' }}
              />
            ) : (
              <TeamDropdown
                isSnapshot={true}
                setSelectedTeamId={setSelectedTeamId}
                setSelectedTeamName={setTeamName}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={8} lg={7} xl={7}>
          <Box className={classes.chartContainer}>
            {isLoading ? (
              graphLoader
            ) : (
              <HighchartsReact
                allowChartUpdate={true}
                options={options}
                ref={chartRef}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={6} sm={4} md={6} lg={2} xl={2}>
          {isLoading ? (
            <div className={classes.statLoader}>
              <Skeleton variant="rectangular" />
              <Skeleton variant="rectangular" />
            </div>
          ) : (
            <div className={classes.callConnections}>
              <StatPanel
                agentTotal={agentSeries?.calls}
                teamAverage={teamCallMetricsCached?.current?.calls?.average}
                type={'Calls'}
              />
              <StatPanel
                agentTotal={agentSeries?.connections}
                teamAverage={
                  teamCallMetricsCached?.current?.connections?.average
                }
                type={'Connections'}
              />
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default AgentCallMetricsBar;
